import { PaymentMethodValid } from '@core/enums';
import { NotificationEnum } from '@core/enums';
import { CheckoutOrderValidationError } from '../enum/checkout-order-validation.enum';
import { CheckoutValidationException } from '../checkout-validation.exception';
import { CheckoutValidationContext, CheckoutValidationRule } from '../interfaces/checkout-validation-flow.interface';
import { CheckoutAsyncActionType } from '../enum/checkout-flow.enum';
import {
	CheckoutOrderValidationContext,
} from '../interfaces/checkout-order-validation.interface';
import { CheckoutOrderValidationRule } from '../enum/checkout-order-validation.enum';
import {
	getCheckoutOrderValidationMessage,
	prepareCheckoutOrder,
	updateCheckoutCartPrices,
} from '../util';
import {
	checkoutOrderDataRule,
} from './required-order-data.domain';
import { checkoutOrderDeliveryZoneRule } from './delivery-zone.domain';
import { checkoutOrderTypeRule } from './order-type.domain';
import { checkoutOrderProductsAvailabilityRule } from './products-availability.domain';
import { checkoutOrderServiceAvailabilityRule } from './service-availability.domain';
import { checkoutOrderWorkDayRule } from './store-workday.domain';

export const checkoutAsyncErrorRule: CheckoutValidationRule = context => {
	if (context.failure) return context;

	const failedError =
		context.asyncResults.availability.type === CheckoutAsyncActionType.AvailabilityFailure
			? context.asyncResults.availability.error
			: !context.asyncResults.priceLookup.succeeded
				? context.asyncResults.priceLookup.error
				: context.asyncResults.businessDay.type === CheckoutAsyncActionType.BusinessDayFailure
					? context.asyncResults.businessDay.error
					: !context.asyncResults.serviceAvailability.succeeded
						? context.asyncResults.serviceAvailability.error
						: !context.asyncResults.stockUpdate.succeeded
							? context.asyncResults.stockUpdate.error
							: null;
	const normalizedContext: CheckoutValidationContext = {
		...context,
		isAvailabilitySuccessful:
			context.asyncResults.availability.type === CheckoutAsyncActionType.AvailabilitySuccess,
		availabilityErrorMessage:
			context.asyncResults.availability.type === CheckoutAsyncActionType.AvailabilityFailure
				? getAsyncErrorMessage(context.asyncResults.availability.error)
				: null,
		priceLookupSucceeded: context.asyncResults.priceLookup.succeeded,
		priceLookupErrorMessage: context.asyncResults.priceLookup.succeeded
			? null
			: getAsyncErrorMessage(context.asyncResults.priceLookup.error),
		updatedMenuItems: context.asyncResults.priceLookup.succeeded
			? context.asyncResults.priceLookup.value.updatedMenuItems
			: [],
		priceChanges: context.asyncResults.priceLookup.succeeded
			? context.asyncResults.priceLookup.value.priceChanges
			: [],
		workDayChecked:
			context.asyncResults.businessDay.type === CheckoutAsyncActionType.BusinessDaySuccess,
		workDay:
			context.asyncResults.businessDay.type === CheckoutAsyncActionType.BusinessDaySuccess
				? context.asyncResults.businessDay.workDay
				: null,
		serviceAvailabilityChecked: true,
		serviceAvailability: context.asyncResults.serviceAvailability.succeeded
			? context.asyncResults.serviceAvailability.value
			: null,
		updatedStockItems: context.asyncResults.stockUpdate.succeeded
			? context.asyncResults.stockUpdate.value
			: null,
	};
	if (failedError === null) return normalizedContext;

	return {
		...normalizedContext,
		failure: new CheckoutValidationException(
			getAsyncErrorMessage(failedError) || 'The order could not be validated',
			'No se pudo validar la orden',
			NotificationEnum.Error
		),
	};
};

function getAsyncErrorMessage(error: unknown): string | null {
	if (error === null || error === undefined) return null;
	if (typeof error === 'string') return error.trim() || 'The order could not be validated';
	if (error && typeof error === 'object' && 'message' in error) {
		const message = error.message;
		if (typeof message === 'string' && message.trim()) return message;
	}
	if (error && typeof error === 'object' && 'error' in error) {
		const detail = error.error;
		if (typeof detail === 'string' && detail.trim()) return detail;
		if (Array.isArray(detail)) {
			const messages = detail.filter(
				(message): message is string => typeof message === 'string' && message.trim().length > 0
			);
			if (messages.length > 0) return messages.join(', ');
		}
	}

	return 'The order could not be validated';
}

function applyOrderValidation(
	context: CheckoutValidationContext,
	validationRule: CheckoutOrderValidationRule
): CheckoutValidationContext {
	if (context.failure || !context.orderValidationContext) return context;

	const error = validationRule(context.orderValidationContext);
	return error
		? {
				...context,
				failure: new CheckoutValidationException(
					getCheckoutOrderValidationMessage(error, context.unavailableProducts),
					error === CheckoutOrderValidationError.DeliveryZoneUnavailable
						? 'Zona de entrega no disponible'
						: 'No se pudo validar la orden',
					error === CheckoutOrderValidationError.StoreClosed
						? NotificationEnum.Warning
						: NotificationEnum.Error
				),
			}
		: context;
};

export const checkoutAvailabilityResultRule: CheckoutValidationRule = context => {
	if (context.failure || (context.isAvailabilitySuccessful && context.priceLookupSucceeded)) {
		return context;
	}

	return {
		...context,
		failure: new CheckoutValidationException(
			context.priceLookupErrorMessage ||
				context.availabilityErrorMessage ||
				'Product availability could not be verified.',
			'Producto no disponible',
			NotificationEnum.Error
		),
	};
};

export const checkoutUnavailableProductsRule: CheckoutValidationRule = context => {
	if (context.failure || context.unavailableProducts.length === 0) return context;

	return {
		...context,
		failure: new CheckoutValidationException(
			getCheckoutOrderValidationMessage(
				CheckoutOrderValidationError.ProductsUnavailable,
				context.unavailableProducts
			),
			'Producto no disponible',
			NotificationEnum.Error
		),
	};
};

export const checkoutPriceChangesRule: CheckoutValidationRule = context => {
	if (context.failure || context.priceChanges.length === 0) return context;

	return {
		...context,
		failure: new CheckoutValidationException(
			`The prices of the following products have changed: ${context.priceChanges
				.map(charge => charge.nombre)
				.join(', ')}. Please review your order before continuing.`,
			'Price Change Detected',
			NotificationEnum.Warning,
			updateCheckoutCartPrices(context.cartItems, context.updatedMenuItems)
		),
	};
};

export const checkoutOrderValidationContextRule: CheckoutValidationRule = context => {
	if (context.failure || context.orderValidationContext) return context;

	const { orderForm, unavailableProducts } = context;
	const orderValidationContext: CheckoutOrderValidationContext = {
		orderTypeName: orderForm.typeOrder?.nombre,
		deliveryZoneCount: orderForm.deliveryZoneList?.length ?? 0,
		hasUserLocation: !!orderForm.userLocation,
		storeId: orderForm.storeData?.idLocal,
		localChannelId: orderForm.localChannelId,
		hasOrderType: !!orderForm.typeOrder,
		hasCartTotals: !!orderForm.cartTotals,
		unavailableProducts,
		workDayIsOpen: context.workDayChecked ? !!context.workDay?.estaAbierto : undefined,
		serviceAvailability: context.serviceAvailabilityChecked
			? context.serviceAvailability ?? undefined
			: undefined,
	};

	return { ...context, orderValidationContext };
};

export const checkoutOrderProductsFlowRule: CheckoutValidationRule = context =>
	applyOrderValidation(context, checkoutOrderProductsAvailabilityRule);

export const checkoutOrderDeliveryZoneFlowRule: CheckoutValidationRule = context =>
	applyOrderValidation(context, checkoutOrderDeliveryZoneRule);

export const checkoutOrderTypeFlowRule: CheckoutValidationRule = context =>
	applyOrderValidation(context, checkoutOrderTypeRule);

export const checkoutOrderDataFlowRule: CheckoutValidationRule = context =>
	applyOrderValidation(context, checkoutOrderDataRule);

export const checkoutOrderWorkDayFlowRule: CheckoutValidationRule = context => {
	if (!context.workDayChecked) return context;
	return applyOrderValidation(context, checkoutOrderWorkDayRule);
};

export const checkoutOrderServiceAvailabilityFlowRule: CheckoutValidationRule = context => {
	if (!context.serviceAvailabilityChecked) return context;
	return applyOrderValidation(context, checkoutOrderServiceAvailabilityRule);
};

export const checkoutPaymentDataRule: CheckoutValidationRule = context => {
	if (context.failure || context.orderForm.paymentMethod !== PaymentMethodValid.CARD) return context;

	const hasCardToken = !!(
		context.selectedCardToken ||
		context.defaultCardToken ||
		context.associatedCardToken
	);
	const hasWorkDayId = !context.workDayChecked || !!context.workDay?.idlaboral;

	return hasCardToken && hasWorkDayId
		? context
		: {
				...context,
				failure: new CheckoutValidationException(
					getCheckoutOrderValidationMessage(
						CheckoutOrderValidationError.PaymentDataIncomplete,
						context.unavailableProducts
					),
					'Payment information incomplete',
					NotificationEnum.Error
				),
			};
};

export const checkoutOrderPreparationRule: CheckoutValidationRule = context => {
	if (
		context.failure ||
		!context.workDayChecked ||
		!context.serviceAvailabilityChecked ||
		!context.workDay
	) {
		return context;
	}

	const preparedOrder = prepareCheckoutOrder({
		order: context.orderForm,
		workDay: context.workDay,
	});

	return { ...context, preparedOrder, isReady: true };
};
